export type LabelFieldGroup = 'Material' | 'Supplier' | 'Customer' | 'Grn' | 'Production' | 'Packing' | 'WeightProduction' | 'System';
export type LabelPrintContext = 'grn' | 'outbound' | 'production' | 'packing' | 'weightProduction';

export interface LabelFieldDef {
  key: string;
  group: LabelFieldGroup;
}

export const MATERIAL_LABEL_FIELDS: LabelFieldDef[] = [
  { key: 'MaterialCode', group: 'Material' },
  { key: 'MaterialName', group: 'Material' },
  { key: 'CategoryName', group: 'Material' },
  { key: 'UomCode', group: 'Material' },
  { key: 'Barcode', group: 'Material' },
  { key: 'HsnCode', group: 'Material' },
  { key: 'GstRate', group: 'Material' },
  { key: 'ManufacturerName', group: 'Material' },
  { key: 'AbcClass', group: 'Material' },
];

export const SUPPLIER_LABEL_FIELDS: LabelFieldDef[] = [
  { key: 'SupplierCode', group: 'Supplier' },
  { key: 'SupplierName', group: 'Supplier' },
  { key: 'SupplierGstin', group: 'Supplier' },
  { key: 'SupplierAddress', group: 'Supplier' },
  { key: 'SupplierContactPerson', group: 'Supplier' },
  { key: 'SupplierPhone', group: 'Supplier' },
];

export const CUSTOMER_LABEL_FIELDS: LabelFieldDef[] = [
  { key: 'CustomerCode', group: 'Customer' },
  { key: 'CustomerName', group: 'Customer' },
  { key: 'CustomerGstin', group: 'Customer' },
  { key: 'CustomerAddress', group: 'Customer' },
  { key: 'CustomerContactPerson', group: 'Customer' },
  { key: 'CustomerPhone', group: 'Customer' },
];

// Every column GoodsReceiptNote (header) + GrnLine (line) actually carries, minus internal
// FK/id-only fields (CompanyId, GateEntryId, PurchaseOrderLineId, ...) that have no display value,
// and minus LabelPrintedQty (a running bookkeeping counter of how many labels have ALREADY been
// printed for this line — circular/meaningless to print ON the label currently being printed).
// ClientName is 3PL-only (blank for a non-3PL company's GRN, same "resolves to empty string"
// pattern already used for other optional fields elsewhere) — see GrnLabelPrintPage.tsx's
// buildBaseSampleData for where each of these is actually resolved from real GRN/line data.
export const GRN_LABEL_FIELDS: LabelFieldDef[] = [
  { key: 'GrnNumber', group: 'Grn' },
  { key: 'PurchaseOrderNumber', group: 'Grn' },
  { key: 'WarehouseName', group: 'Grn' },
  { key: 'GrnDate', group: 'Grn' },
  { key: 'VehicleNo', group: 'Grn' },
  { key: 'InwardDocNo', group: 'Grn' },
  { key: 'Status', group: 'Grn' },
  { key: 'ClientName', group: 'Grn' },
  { key: 'BatchNo', group: 'Grn' },
  { key: 'MfgDate', group: 'Grn' },
  { key: 'ExpiryDate', group: 'Grn' },
  { key: 'OrderedQty', group: 'Grn' },
  { key: 'ReceivedQty', group: 'Grn' },
  { key: 'AcceptedQty', group: 'Grn' },
  { key: 'RejectedQty', group: 'Grn' },
  { key: 'UnitPrice', group: 'Grn' },
];

// Every column ProductionReceipt (header) + ProductionReceiptLine (line, its "child table") actually
// carries, minus internal FK/id-only fields (CompanyId, ProductionOrderId, MaterialRequestId, ...)
// and LabelPrintedQty (a running bookkeeping counter, same reason GrnLine.LabelPrintedQty is
// excluded from GRN_LABEL_FIELDS — see there). No OrderedQty/ClientName analog exists here: a
// receipt line has no "ordered" quantity of its own (that concept lives on ProductionOrderLine.
// RequiredQty, a different child collection this label print screen never resolves), and
// ProductionReceiptDto carries no ClientId/ClientName today. Status and RejectedQty end up reachable
// under the 'Grn' heading instead of this group in the designer's merged field picker (see
// PropertiesPanel.tsx's bindableFieldsFor) since GRN_LABEL_FIELDS already declares those same key
// names — same "one shared token, resolved differently per actual print context" treatment already
// applied to WarehouseName/BatchNo/MfgDate/ExpiryDate/AcceptedQty below (GrnLabelPrintPage.tsx
// resolves {{Status}} from the GRN's own status, ProductionLabelPrintPage.tsx resolves it from the
// receipt's). See ProductionLabelPrintPage.tsx's buildBaseSampleData for where each is actually
// resolved from real receipt/line data.
export const PRODUCTION_LABEL_FIELDS: LabelFieldDef[] = [
  { key: 'ReceiptNumber', group: 'Production' },
  { key: 'ProductionOrderNumber', group: 'Production' },
  { key: 'WarehouseName', group: 'Production' },
  { key: 'ReceiptDate', group: 'Production' },
  { key: 'Status', group: 'Production' },
  { key: 'BatchNo', group: 'Production' },
  { key: 'MfgDate', group: 'Production' },
  { key: 'ExpiryDate', group: 'Production' },
  { key: 'ProducedQty', group: 'Production' },
  { key: 'AcceptedQty', group: 'Production' },
  { key: 'RejectedQty', group: 'Production' },
  { key: 'UnitCost', group: 'Production' },
];

// Mirrors the Packing Management screen's own tables — PackingTaskDto (task-level, same across
// every label in a job) and PackageDto/PackingSourceLineDto (per-package, resolved against
// whichever package a given physical label is for — see PackingLabelPrintPage.tsx's
// sampleDataForPackage). MaterialCode/MaterialName come from MATERIAL_LABEL_FIELDS instead of being
// redeclared here (see LABEL_FIELDS_BY_CONTEXT.packing below). Qty is listed right after PackageNo
// (its natural "package number, then its quantity" order) even though it's really the same {{Qty}}
// token as SYSTEM_LABEL_FIELDS's own entry — regrouped here as 'Packing' and positioned next to
// PackageNo so it's easy to find instead of buried at the end under a separate "System" header.
export const PACKING_LABEL_FIELDS: LabelFieldDef[] = [
  { key: 'PackingTaskNumber', group: 'Packing' },
  { key: 'PickListNumber', group: 'Packing' },
  { key: 'OrderNumber', group: 'Packing' },
  { key: 'SourceNumber', group: 'Packing' },
  { key: 'Status', group: 'Packing' },
  { key: 'PackageNo', group: 'Packing' },
  { key: 'Qty', group: 'Packing' },
  { key: 'Weight', group: 'Packing' },
  { key: 'Remarks', group: 'Packing' },
  { key: 'CarrierName', group: 'Packing' },
  { key: 'RequestedQty', group: 'Packing' },
  { key: 'IssuedQty', group: 'Packing' },
];

// Mirrors WeightProductionEntryDto — one weighing = one unit = one label, so unlike GRN/Production/
// Packing there is no separate "line vs header" split and no printed-quantity counter to exclude.
// BarcodeNo is listed explicitly (rather than relying on SYSTEM_LABEL_FIELDS) since it's the actual
// reserved, never-regenerated value the entry was created with — see WeightProductionEntry.BarcodeNo's
// doc comment — not a design-time auto-generate sequence. See WeightProductionKioskPage.tsx /
// WeightProductionLabelPrintPage.tsx for where each of these is resolved from real entry data.
export const WEIGHT_PRODUCTION_LABEL_FIELDS: LabelFieldDef[] = [
  { key: 'BarcodeNo', group: 'WeightProduction' },
  { key: 'OrderNumber', group: 'WeightProduction' },
  { key: 'StandardWeight', group: 'WeightProduction' },
  { key: 'MinWeight', group: 'WeightProduction' },
  { key: 'MaxWeight', group: 'WeightProduction' },
  { key: 'ActualWeight', group: 'WeightProduction' },
  { key: 'WeightUom', group: 'WeightProduction' },
  { key: 'ToleranceStatus', group: 'WeightProduction' },
  { key: 'WarehouseName', group: 'WeightProduction' },
  { key: 'HardwareStationName', group: 'WeightProduction' },
  { key: 'OperatorName', group: 'WeightProduction' },
  { key: 'BatchNo', group: 'WeightProduction' },
  { key: 'ExpiryDate', group: 'WeightProduction' },
  { key: 'ProductionDate', group: 'WeightProduction' },
];

export const SYSTEM_LABEL_FIELDS: LabelFieldDef[] = [
  { key: 'PrintDate', group: 'System' },
  { key: 'PrintTime', group: 'System' },
  // The quantity THIS specific physical label covers — varies per label, not per template. Auto
  // mode fills it from Material.stuffingQty groupings, Manual mode from the operator's own split
  // (see GrnLabelPrintPage.tsx). Distinct from ReceivedQty/AcceptedQty, which are the whole line's
  // totals and identical across every label in the job.
  { key: 'Qty', group: 'System' },
];

/** Which field groups are offered depends on which print screen a template is meant for — a GRN
 * (goods receipt) has no Customer anywhere on it, while a future outbound/dispatch label would
 * have Customer but no GRN-specific fields. Extend this map (and GrnLabelPrintPage-style resolver)
 * when a new print context is added, rather than flattening every field into one list that offers
 * bindings with no real data behind them on a given screen — that was the exact bug this replaces
 * (see GrnLabelPrintPage.tsx's sampleData, which used to hardcode several of these to '').*/
export const LABEL_FIELDS_BY_CONTEXT: Record<LabelPrintContext, LabelFieldDef[]> = {
  grn: [...MATERIAL_LABEL_FIELDS, ...SUPPLIER_LABEL_FIELDS, ...GRN_LABEL_FIELDS, ...SYSTEM_LABEL_FIELDS],
  outbound: [...MATERIAL_LABEL_FIELDS, ...CUSTOMER_LABEL_FIELDS, ...SYSTEM_LABEL_FIELDS],
  production: [...MATERIAL_LABEL_FIELDS, ...PRODUCTION_LABEL_FIELDS, ...SYSTEM_LABEL_FIELDS],
  // Only MaterialCode/MaterialName from the Material group — PackingLabelPrintPage resolves those
  // two directly off the package's own denormalized fields, not a full Material master lookup
  // (unlike GRN/Production), so the other Material fields (Category/UOM/HSN/...) aren't offered
  // here to avoid a binding with no real data behind it. SYSTEM_LABEL_FIELDS's own Qty is dropped
  // (PACKING_LABEL_FIELDS already has its own, positioned next to PackageNo) to avoid a duplicate
  // entry. Kept contiguous by group (Material, then Packing, then System) since MUI's Autocomplete
  // groupBy expects pre-sorted options.
  packing: [
    ...MATERIAL_LABEL_FIELDS.filter((f) => f.key === 'MaterialCode' || f.key === 'MaterialName'),
    ...PACKING_LABEL_FIELDS,
    ...SYSTEM_LABEL_FIELDS.filter((f) => f.key !== 'Qty'),
  ],
  // SYSTEM_LABEL_FIELDS's Qty is dropped — ActualWeight is the one-and-only quantity concept here,
  // already in WEIGHT_PRODUCTION_LABEL_FIELDS, same "no duplicate entry" treatment as packing above.
  weightProduction: [...MATERIAL_LABEL_FIELDS, ...WEIGHT_PRODUCTION_LABEL_FIELDS, ...SYSTEM_LABEL_FIELDS.filter((f) => f.key !== 'Qty')],
};

/** Every field across every context — used for the designer's own preview (LabelCanvas.tsx),
 * since a template isn't locked to one print context at design time. */
export const ALL_LABEL_FIELDS: LabelFieldDef[] = [
  ...MATERIAL_LABEL_FIELDS,
  ...SUPPLIER_LABEL_FIELDS,
  ...CUSTOMER_LABEL_FIELDS,
  ...GRN_LABEL_FIELDS,
  ...PRODUCTION_LABEL_FIELDS,
  ...PACKING_LABEL_FIELDS,
  ...WEIGHT_PRODUCTION_LABEL_FIELDS,
  ...SYSTEM_LABEL_FIELDS,
];
